import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  getAvailableShippingMethods,
  setSelectedShippingMethods,
} from '../../../slices/shippingMethods';
import {
  getFreeOrMinorPriceShippingMethodFromAvailable,
  getSelectedShippingMethodsForAvailables,
} from '../../../slices/shippingMethods/shippingMethods.utils';

import usePrevious from '../../../hooks/usePrevious';
import useQueryParams from '../../../hooks/useQueryParams';
import { isShippingAddressValid } from '../../../utils';

import DeliveryMethodsStep from '../DeliveryMethodsStep';

import {
  IN_HOME_SETUP_QUALIFIED_SHIPPING_METHOD_KEY,
  STANDARD_SHIPPING_KEY,
  REJUVENATE_COLLECTION_PRODUCT_KEYS,
} from '../../../utils/common/constants';
import { selectIsOverwritePaidInHomeShippingABTest } from '../../../store/selectors/cart';
import { getCookie } from '../../../utils/common';

const SHIPPING_METHODS_STATUS_LOADING = 'loading';

const AutoFillDeliveryMethodsStep = ({
  showCompleted,
  isCompleted,
  showEditing,
  onClickSave,
  onClickCancel,
  isEditing,
  shippingAddress,
}) => {
  const [
    selectedShippingMethodsClearedDueToRecheckNeeded,
    setSelectedShippingMethodsClearedDueToRecheckNeeded,
  ] = useState(false);
  const [
    selectedShippingMethodsClearedBecauseDoesntCoverAvailables,
    setSelectedShippingMethodsClearedBecauseDoesntCoverAvailables,
  ] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { locale } = useIntl();

  const dispatch = useDispatch();
  const accounts = useSelector((state) => state.accounts);
  const { selectedShippingMethods } = useSelector(
    (state) => state.shippingMethods,
  );

  const {
    availableShippingMethods,
    shippingMethodsStatus,
    needToRecheckSelectedShippingMethods,
    cartStatus,
  } = useSelector(({ shippingMethods, cart }) => ({
    availableShippingMethods: shippingMethods.availableShippingMethods,
    shippingMethodsStatus: shippingMethods.status,
    needToRecheckSelectedShippingMethods:
      shippingMethods.needToRecheckSelectedShippingMethods,
    cartStatus: cart.status,
  }));

  const customerAddress = accounts?.customerAddress;
  const isCartLoading = cartStatus === 'loading';
  const previousCustomerId = usePrevious(accounts?.customerId);
  const [
    defaultShippingMethodsForCustomer,
    setDefaultShippingMethodsForCustomer,
  ] = useState(false);

  const cart = useSelector((state) => state.cart);

  const isRejuvenateMattress = cart?.lineItems.find((lineItem) =>
    REJUVENATE_COLLECTION_PRODUCT_KEYS.includes(lineItem.productKey),
  );

  const isOverwritePaidInHomeShippingABTest = useSelector(
    selectIsOverwritePaidInHomeShippingABTest,
  );

  const availableShippingMethodsSorted = availableShippingMethods.map(
    (availableShippingMethod) =>
      availableShippingMethod.key ===
      IN_HOME_SETUP_QUALIFIED_SHIPPING_METHOD_KEY
        ? {
            ...availableShippingMethod,
            groups: availableShippingMethod.groups.map((group) => ({
              ...group,
              shippingMethods: [...group.shippingMethods].sort((a, b) => {
                if (isOverwritePaidInHomeShippingABTest) {
                  if (a.key === STANDARD_SHIPPING_KEY) {
                    return +1;
                  }
                  return -1;
                }

                if (a.price.centAmount === b.price.centAmount) {
                  if (a.key === STANDARD_SHIPPING_KEY) {
                    if (isRejuvenateMattress) {
                      return +1;
                    }
                    return -1;
                  }

                  return +1;
                }

                return a.price.centAmount - b.price.centAmount;
              }),
            })),
          }
        : availableShippingMethod,
  );

  const defaultSelectedShippingMethods =
    availableShippingMethodsSorted.map((availableShippingMethod) =>
      getFreeOrMinorPriceShippingMethodFromAvailable({
        availableShippingMethod,
      }),
    );

  // RE-ENABLE IF NEED TO NOT DEFAULT LARGE SHIPPING METHODS
  // const defaultSelectedShippingMethods =
  //   defaultSelectedShippingMethodsWithoutSelectedFilterApplied?.map(
  //     (defaultSelectedShippingMethod, defaultSelectedShippingMethodIndex) => ({
  //       ...defaultSelectedShippingMethod,
  //       groups: defaultSelectedShippingMethod.groups.map(
  //         (group, groupIndex) => ({
  //           ...group,
  //           key:
  //             availableShippingMethodsSorted?.[
  //               defaultSelectedShippingMethodIndex
  //             ]?.groups?.[groupIndex]?.shippingMethods?.length === 1
  //               ? group.key
  //               : undefined,
  //         }),
  //       ),
  //     }),
  //   ) ?? [];

  const defaultSelectedShippingMethodsAsJSON = JSON.stringify(
    defaultSelectedShippingMethods,
  );

  const customerId = cart.customerId || localStorage.getItem('customerId');

  const availableShippingMethodsWithProductQuantityFromLineItems =
    availableShippingMethods?.map((shippingMethod) => ({
      ...shippingMethod,
      groups: shippingMethod.groups.map((shippingMethodGroup) => ({
        ...shippingMethodGroup,
        products: shippingMethodGroup.products.map(
          (shippingMethodGroupProduct) => ({
            ...shippingMethodGroupProduct,
            quantity: cart.lineItems.find(
              (lineItem) =>
                lineItem.variant?.sku === shippingMethodGroupProduct.sku,
            )?.quantity,
          }),
        ),
      })),
    }));

  const isLoadingShippingMethods =
    shippingMethodsStatus === SHIPPING_METHODS_STATUS_LOADING;

  const selectShippingMethodHandler = useCallback(
    ({ parentKey, groupId, key, name, cost, needsRemoval }) => {
      const shippingMethodToChange = selectedShippingMethods.find(
        (selectedShippingMethod) =>
          selectedShippingMethod.parentKey === parentKey,
      );

      dispatch(
        setSelectedShippingMethods([
          ...selectedShippingMethods.filter(
            (selectedShippingMethod) =>
              selectedShippingMethod.parentKey !==
              shippingMethodToChange.parentKey,
          ),
          {
            parentKey,
            groups:
              shippingMethodToChange?.groups?.map((group, index) =>
                index === groupId
                  ? {
                      key,
                      name,
                      cost,
                      needsRemoval,
                      skus: group.skus,
                    }
                  : group,
              ) ?? [],
          },
        ]),
      );
    },
    [dispatch, selectedShippingMethods],
  );

  const selectedShippingMethodsAsJSON = JSON.stringify(selectedShippingMethods);

  const selectedShippingMethodsForAvailables =
    getSelectedShippingMethodsForAvailables({
      cart,
      availableShippingMethods:
        availableShippingMethodsWithProductQuantityFromLineItems,
    });

  const selectedShippingMethodsForAvailablesAsJSON = JSON.stringify(
    selectedShippingMethodsForAvailables,
  );

  const allAvailableShippingMethodsHaveOneSelected =
    selectedShippingMethodsForAvailables?.every((selectedShippingMethod) =>
      selectedShippingMethod?.groups?.every(
        (group) => !!group.selectedShippingMethod,
      ),
    );

  const { paymentMethodData, paypalOrderId, applePaymentData } = useSelector(
    (state) => state.payments,
  );

  const [amazonPayApproved, amazonPayCheckoutCreated] = useQueryParams([
    'amazonPayApproved',
    'amazonPayCheckoutCreated',
  ]);

  const isPaymentStepComplete =
    !!paymentMethodData ||
    (!!paypalOrderId && isShippingAddressValid(shippingAddress)) ||
    !!amazonPayCheckoutCreated ||
    !!amazonPayApproved ||
    !!applePaymentData;

  const previousIsPaymentStepComplete = usePrevious(isPaymentStepComplete);
  const isPaymentStepCompleteChangedFromFalseToTrue =
    !previousIsPaymentStepComplete && !!isPaymentStepComplete;

  useEffect(() => {
    if (showEditing && !isCartLoading) {
      dispatch(
        getAvailableShippingMethods({
          locale,
          overwritePaidInHomeSetupShipping: isOverwritePaidInHomeShippingABTest,
          customInHomeSetupPricing: getCookie(
            process.env.CUSTOM_IN_HOME_SETUP_PRICING_AB_TEST_COOKIE_NAME,
          )?.value,
        }),
      );
      setSelectedShippingMethodsClearedDueToRecheckNeeded(false);
      setSelectedShippingMethodsClearedBecauseDoesntCoverAvailables(false);
    }
  }, [
    dispatch,
    locale,
    showEditing,
    isCartLoading,
    isOverwritePaidInHomeShippingABTest,
  ]);

  const saveDeliveryMethodHandler = useCallback(
    async ({ deliveryMethods }) => {
      setIsSaving(true);
      await onClickSave({ deliveryMethods });
      setIsSaving(false);
      setSelectedShippingMethodsClearedDueToRecheckNeeded(false);
      setSelectedShippingMethodsClearedBecauseDoesntCoverAvailables(false);
    },
    [onClickSave],
  );

  const cancelDeliveryMethodHandler = () => {
    if (allAvailableShippingMethodsHaveOneSelected) {
      const newSelectedShippingMethods =
        selectedShippingMethodsForAvailables.map((selectedShippingMethod) => ({
          parentKey: selectedShippingMethod.key,
          groups: selectedShippingMethod.groups.map((group) => ({
            key: group.selectedShippingMethod?.key,
            name: group.selectedShippingMethod?.name,
            cost: group.selectedShippingMethod?.price?.centAmount,
            needsRemoval: group.selectedShippingMethod?.needsRemoval,
            skus: group.products.map((product) => product.sku),
          })),
        }));
      dispatch(setSelectedShippingMethods(newSelectedShippingMethods));
    } else {
      dispatch(setSelectedShippingMethods(defaultSelectedShippingMethods));
    }
    onClickCancel();
  };

  const customerLoggedIn = customerId && customerId !== previousCustomerId;

  // for authenticated sessions, it sets the default Selected Shipping Methods
  const customerLogsInAndItDoesntHaveSelectedDefaultShippingMethodsForCustomer =
    customerLoggedIn &&
    !defaultShippingMethodsForCustomer &&
    defaultSelectedShippingMethods?.length > 0 &&
    selectedShippingMethods?.length === 0 &&
    !isEditing;

  const customerIsLoggedInAndItDoesntHaveSelectedDefaultShippingMethodsForCustomer =
    customerId &&
    customerId === previousCustomerId &&
    selectedShippingMethods?.length > 0 &&
    defaultSelectedShippingMethods?.length > 0 &&
    !defaultShippingMethodsForCustomer &&
    customerAddress?.streetName &&
    customerAddress?.streetName !== '' &&
    !isEditing;

  // the payment step is complete, it sets the default Selected Shipping Methods
  const paymentMethodStepIsCompleteAndItDoesntHaveSelectedShippingMethods =
    showEditing &&
    isPaymentStepComplete &&
    !amazonPayApproved && // Don't auto set the shippingMethods if we are returning from the 2nd modal of Amazon Pay (Place Order)
    defaultSelectedShippingMethods?.length > 0 &&
    selectedShippingMethods?.length === 0;

  // the payment step becomes complete, it sets the already selected Shipping Methods
  // or the defaults
  const paymentMethodStepBecomesComplete =
    showEditing &&
    isPaymentStepCompleteChangedFromFalseToTrue &&
    !amazonPayApproved; // Don't auto set the shippingMethods if we are returning from the 2nd modal of Amazon Pay (Place Order)

  const selectedShippingMethodsNeedsToBeRechecked =
    showEditing &&
    needToRecheckSelectedShippingMethods &&
    !selectedShippingMethodsClearedDueToRecheckNeeded &&
    availableShippingMethods.length > 0 &&
    !amazonPayApproved; // Don't auto set the shippingMethods if we are returning from the 2nd modal of Amazon Pay (Place Order)

  const someOfTheAvailableShippingMethodsDoesntHaveOneSelected =
    showEditing &&
    selectedShippingMethodsForAvailables?.length > 0 &&
    selectedShippingMethods.length > 0 &&
    !allAvailableShippingMethodsHaveOneSelected &&
    !selectedShippingMethodsClearedBecauseDoesntCoverAvailables;

  // this is the case when some of the available shipping methods has no corresponding
  // selected shipping methods in the current cart
  const someOfTheAvailableShippingMethodsHaveNoCorrespondingShippingMethodsInCurrentCart =
    showEditing &&
    selectedShippingMethodsForAvailables?.length > 0 &&
    !allAvailableShippingMethodsHaveOneSelected &&
    !selectedShippingMethodsClearedBecauseDoesntCoverAvailables;

  const selectedShippingMethodsNeedsToBeUpdated =
    showEditing &&
    selectedShippingMethods?.length === 0 &&
    selectedShippingMethodsForAvailables?.length > 0 &&
    allAvailableShippingMethodsHaveOneSelected;

  const noSelectedShippingMethods =
    showEditing &&
    defaultSelectedShippingMethods?.length > 0 &&
    selectedShippingMethods?.length === 0;

  useEffect(() => {
    let defaultSelectedShippingMethodsValue;
    let selectedShippingMethodsValue;
    let selectedShippingMethodsForAvailablesValue;

    try {
      defaultSelectedShippingMethodsValue = JSON.parse(
        defaultSelectedShippingMethodsAsJSON,
      );
    } catch (error) {
      defaultSelectedShippingMethodsValue = [];
    }

    try {
      selectedShippingMethodsValue = JSON.parse(selectedShippingMethodsAsJSON);
    } catch (error) {
      selectedShippingMethodsValue = [];
    }

    try {
      selectedShippingMethodsForAvailablesValue = JSON.parse(
        selectedShippingMethodsForAvailablesAsJSON,
      );
    } catch (error) {
      selectedShippingMethodsForAvailablesValue = [];
    }

    if (
      customerLogsInAndItDoesntHaveSelectedDefaultShippingMethodsForCustomer
    ) {
      setDefaultShippingMethodsForCustomer(true);
      dispatch(setSelectedShippingMethods(defaultSelectedShippingMethodsValue));
      saveDeliveryMethodHandler({
        deliveryMethods: defaultSelectedShippingMethodsValue,
      });
    } else if (
      customerIsLoggedInAndItDoesntHaveSelectedDefaultShippingMethodsForCustomer
    ) {
      setDefaultShippingMethodsForCustomer(true);
      saveDeliveryMethodHandler({
        deliveryMethods: defaultSelectedShippingMethodsValue,
      });
    } else if (
      paymentMethodStepIsCompleteAndItDoesntHaveSelectedShippingMethods
    ) {
      dispatch(setSelectedShippingMethods(defaultSelectedShippingMethodsValue));
      saveDeliveryMethodHandler({
        deliveryMethods: defaultSelectedShippingMethodsValue,
      });
    } else if (paymentMethodStepBecomesComplete) {
      if (selectedShippingMethodsValue?.length > 0) {
        saveDeliveryMethodHandler({
          deliveryMethods: selectedShippingMethodsValue,
        });
      } else {
        dispatch(
          setSelectedShippingMethods(defaultSelectedShippingMethodsValue),
        );
        saveDeliveryMethodHandler({
          deliveryMethods: defaultSelectedShippingMethodsValue,
        });
      }
    } else if (selectedShippingMethodsNeedsToBeRechecked) {
      if (allAvailableShippingMethodsHaveOneSelected) {
        const selectedShippingMethodsForAvailablesGroups =
          selectedShippingMethodsForAvailablesValue.flatMap((shippingMethod) =>
            shippingMethod.groups.map((group) => ({
              parentKey: shippingMethod.key,
              ...group,
            })),
          );

        const availableShippingMethodsGroups = availableShippingMethods.flatMap(
          (shippingMethod) =>
            shippingMethod.groups.map((group) => ({
              parentKey: shippingMethod.key,
              ...group,
            })),
        );

        const selectedShippingMethodComparisonAgainstAvailable =
          selectedShippingMethodsForAvailablesGroups.reduce(
            (acc, currentSelectedShippingMethodGroup) => {
              const foundAvailableShippingMethod =
                availableShippingMethodsGroups
                  .find(
                    (availableShippingMethodGroup) =>
                      availableShippingMethodGroup.parentKey ===
                        currentSelectedShippingMethodGroup.parentKey &&
                      availableShippingMethodGroup.id ===
                        currentSelectedShippingMethodGroup.id,
                  )
                  ?.shippingMethods.find(
                    (shippingMethod) =>
                      shippingMethod.key ===
                      currentSelectedShippingMethodGroup.selectedShippingMethod
                        .key,
                  );
              return {
                higherPriceCount:
                  currentSelectedShippingMethodGroup.selectedShippingMethod
                    .price.centAmount <
                  foundAvailableShippingMethod?.price.centAmount
                    ? acc.higherPriceCount + 1
                    : acc.higherPriceCount,
                lowerPriceCount:
                  currentSelectedShippingMethodGroup.selectedShippingMethod
                    .price.centAmount >
                  foundAvailableShippingMethod?.price.centAmount
                    ? acc.lowerPriceCount + 1
                    : acc.lowerPriceCount,
                equalPriceCount:
                  currentSelectedShippingMethodGroup.selectedShippingMethod
                    .price.centAmount ===
                  foundAvailableShippingMethod?.price.centAmount
                    ? acc.equalPriceCount + 1
                    : acc.equalPriceCount,
              };
            },
            {
              higherPriceCount: 0,
              lowerPriceCount: 0,
              equalPriceCount: 0,
            },
          );

        const selectedShippingMethodsWithUpdatedPrices =
          selectedShippingMethodsForAvailablesValue.map(
            (selectedShippingMethod) => ({
              parentKey: selectedShippingMethod.key,
              groups: selectedShippingMethod.groups.map((group) => {
                const foundAvailableShippingMethod =
                  availableShippingMethodsGroups
                    .find(
                      (availableShippingMethodGroup) =>
                        availableShippingMethodGroup.parentKey ===
                          selectedShippingMethod.key &&
                        availableShippingMethodGroup.id === group.id,
                    )
                    ?.shippingMethods.find(
                      (shippingMethod) =>
                        shippingMethod.key ===
                        group.selectedShippingMethod?.key,
                    );
                return {
                  key: foundAvailableShippingMethod?.key,
                  name: foundAvailableShippingMethod?.name,
                  cost: foundAvailableShippingMethod?.price?.centAmount,
                  needsRemoval: group.selectedShippingMethod?.needsRemoval,
                  skus: group.products.map((product) => product.sku),
                };
              }),
            }),
          );

        if (
          selectedShippingMethodComparisonAgainstAvailable.lowerPriceCount +
            selectedShippingMethodComparisonAgainstAvailable.equalPriceCount ===
          selectedShippingMethodsForAvailablesValue.length
        ) {
          dispatch(
            setSelectedShippingMethods(
              selectedShippingMethodsWithUpdatedPrices,
            ),
          );
          saveDeliveryMethodHandler({
            deliveryMethods: selectedShippingMethodsWithUpdatedPrices,
          });
        } else if (
          selectedShippingMethodComparisonAgainstAvailable.higherPriceCount > 0
        ) {
          dispatch(
            setSelectedShippingMethods(
              selectedShippingMethodsWithUpdatedPrices,
            ),
          );
        } else {
          dispatch(
            setSelectedShippingMethods(defaultSelectedShippingMethodsValue),
          );
        }
      } else {
        dispatch(
          setSelectedShippingMethods(defaultSelectedShippingMethodsValue),
        );
      }

      if (!selectedShippingMethodsClearedBecauseDoesntCoverAvailables) {
        // needToRecheckSelectedShippingMethods in redux is cleared when it saves
        // the shippingMethods in redux and CT
        setSelectedShippingMethodsClearedDueToRecheckNeeded(true);
      }
    } else if (someOfTheAvailableShippingMethodsDoesntHaveOneSelected) {
      dispatch(setSelectedShippingMethods(selectedShippingMethodsValue));

      if (!selectedShippingMethodsClearedDueToRecheckNeeded) {
        setSelectedShippingMethodsClearedBecauseDoesntCoverAvailables(true);
      }
    } else if (
      someOfTheAvailableShippingMethodsHaveNoCorrespondingShippingMethodsInCurrentCart
    ) {
      dispatch(setSelectedShippingMethods(defaultSelectedShippingMethodsValue));

      if (!selectedShippingMethodsClearedDueToRecheckNeeded) {
        setSelectedShippingMethodsClearedBecauseDoesntCoverAvailables(true);
      }
    } else if (selectedShippingMethodsNeedsToBeUpdated) {
      const newSelectedShippingMethods =
        selectedShippingMethodsForAvailablesValue.map(
          (selectedShippingMethod) => ({
            parentKey: selectedShippingMethod.key,
            groups: selectedShippingMethod.groups.map((group) => ({
              key: group.selectedShippingMethod?.key,
              name: group.selectedShippingMethod?.name,
              cost: group.selectedShippingMethod?.price?.centAmount,
              needsRemoval: group.selectedShippingMethod?.needsRemoval,
              skus: group.products.map((product) => product.sku),
            })),
          }),
        );
      dispatch(setSelectedShippingMethods(newSelectedShippingMethods));
    } else if (noSelectedShippingMethods) {
      dispatch(setSelectedShippingMethods(defaultSelectedShippingMethodsValue));
    }
  }, [
    allAvailableShippingMethodsHaveOneSelected,
    availableShippingMethods,
    customerIsLoggedInAndItDoesntHaveSelectedDefaultShippingMethodsForCustomer,
    customerLogsInAndItDoesntHaveSelectedDefaultShippingMethodsForCustomer,
    defaultSelectedShippingMethodsAsJSON,
    noSelectedShippingMethods,
    paymentMethodStepBecomesComplete,
    paymentMethodStepIsCompleteAndItDoesntHaveSelectedShippingMethods,
    dispatch,
    saveDeliveryMethodHandler,
    selectedShippingMethodsAsJSON,
    selectedShippingMethodsClearedBecauseDoesntCoverAvailables,
    selectedShippingMethodsClearedDueToRecheckNeeded,
    selectedShippingMethodsForAvailablesAsJSON,
    selectedShippingMethodsNeedsToBeRechecked,
    selectedShippingMethodsNeedsToBeUpdated,
    someOfTheAvailableShippingMethodsDoesntHaveOneSelected,
    someOfTheAvailableShippingMethodsHaveNoCorrespondingShippingMethodsInCurrentCart,
  ]);

  return (
    <DeliveryMethodsStep
      isCompleted={isCompleted}
      selectedShippingMethods={selectedShippingMethods}
      onSelectShippingMethod={selectShippingMethodHandler}
      onSaveDeliveryMethods={saveDeliveryMethodHandler}
      onCancelDeliveryMethod={cancelDeliveryMethodHandler}
      availableShippingMethods={availableShippingMethodsSorted}
      showCompleted={showCompleted}
      showSpinner={showEditing && (isLoadingShippingMethods || isSaving)}
      showDeliveryStep={showEditing && !isLoadingShippingMethods && !isSaving}
      isSaving={isSaving}
    />
  );
};

AutoFillDeliveryMethodsStep.propTypes = {
  showCompleted: PropTypes.bool.isRequired,
  isCompleted: PropTypes.bool,
  showEditing: PropTypes.bool.isRequired,
  onClickSave: PropTypes.func.isRequired,
  onClickCancel: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
  shippingAddress: PropTypes.object.isRequired,
};

AutoFillDeliveryMethodsStep.defaultProps = {
  isCompleted: false,
  isEditing: false,
};

export default AutoFillDeliveryMethodsStep;
